import { Flex, Form, Skeleton, Spin, Typography } from "antd"
import React, { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { type RootState } from "../../../store.ts"
import {
  selectActiveBranch,
  selectActiveBranchId
} from "../../Branches/redux/branches.slice.ts"
import { fetchTeamMembersFilterOptions } from "../../TeamManagementPage/redux/team-members.thunks.ts"
import {
  SettingsCompanyForm,
  type SettingsCompanyFormData
} from "../components/settings-company/SettingsCompanyForm.tsx"
import { fetchUser, updateUser } from "../redux/settings.thunks.ts"

const SettingsCompanyPage = ({
  userId,
  activeBranchId,
  activeBranchName,
  fullUser,
  fetching,
  saveFetching,
  options,
  companyPermissions,
  //
  getFilterOptions,
  getUser,
  updateUser
}: ConnectedProps<typeof connector>) => {
  const [form] = Form.useForm<SettingsCompanyFormData>()

  // `undefined`, never `null` — an absent `team` on the request means
  // "Company HQ" (the caller's own default branch), same contract the
  // backend's `resolve_acting_team` already expects.
  const team = activeBranchId ?? undefined

  const onReset = () => form.resetFields()

  const handleFinish = async ({ logo, ...values }: SettingsCompanyFormData) => {
    try {
      await updateUser({
        id: userId,
        team,
        company_details: {
          // if not src
          ...(typeof logo !== "string" ? { logo } : {}),
          ...values
        }
      }).unwrap()
      form.resetFields()
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    generateDocumentTitle("Settings Company")
  }, [])

  useEffect(() => {
    if (userId) {
      // Re-fetches whenever the workspace selector moves to a different
      // branch (or back to Company HQ) — previously this effect depended
      // only on `userId`, so switching branches while already on this page
      // left whichever branch's data loaded first on screen indefinitely,
      // and every save silently wrote to that same stale branch too. The
      // slice's own `fetchUserRequestId` guard (see `settings.slice.ts`)
      // keeps an older in-flight fetch for a DIFFERENT branch from landing
      // after this one and repainting the page with the wrong data.
      getUser({ id: userId, team })
      getFilterOptions()
    }
  }, [userId, team])

  const heading = useMemo(
    () =>
      activeBranchId
        ? `Branch settings · ${activeBranchName ?? ""}`
        : "Company & System",
    [activeBranchId, activeBranchName]
  )

  return (
    <>
      <Typography.Title level={5} className="mb-3">
        {heading}
      </Typography.Title>
      {fetching ? (
        <Flex flex={1}>
          <Spin fullscreen size="large" />
          <Skeleton.Node className={"size-full"} />
        </Flex>
      ) : (
        <SettingsCompanyForm
          // Remounts the form on every branch switch, instead of merely
          // re-seeding `initialValues` on an antd `Form` instance that
          // stays mounted — antd only applies `initialValues` once, on
          // first mount, and otherwise leaves whatever the previous
          // branch's fields held (including any value the user had typed
          // but not saved) visibly on screen under the new branch's
          // heading until a manual edit happened to touch every field.
          key={team ?? "company-hq"}
          form={form}
          options={options}
          saving={saveFetching}
          readonly={!companyPermissions?.settings_company_edit}
          onReset={onReset}
          onFinish={handleFinish}
          initialValues={fullUser?.company_details}
        />
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.settings.fetching || state.users.fetchingFilterOptions,
  saveFetching:
    state.settings.saveFetching || state.settings.savePasswordFetching,
  options: state.users.filterOptions,
  fullUser: state.settings.user,
  userId: state?.auth?.userInfo?.id,
  activeBranchId: selectActiveBranchId(state),
  activeBranchName: selectActiveBranch(state)?.name,
  companyPermissions:
    state?.auth?.userInfo?.permissions?.codes?.settings?.company
})

const mapDispatchToProps = {
  getFilterOptions: fetchTeamMembersFilterOptions,
  getUser: fetchUser,
  updateUser: updateUser
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SettingsCompanyPage)
